import { FC, useLayoutEffect } from 'react';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { Accordion, Badge, SimpleGrid, Text } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AccordionOptionCard } from '@/components/ui/AccordionOptionCard';
import { UUID } from '@/types/types';
import { setLoading } from '@/store/slices/appSlice';
import { creatorActions } from '@/store/slices/creatorSlice';
import { getFileSrc } from '@/utils/utilities';
import { useStartCollectionMutation } from '@/hooks/useStartCollectionMutation';
import { useContent } from '@/providers/ContentProvider';
import ProjectApi from '@/api/ProjectApi';
import PresetApi from '@/api/PresetApi';

const baseSlug = 'presets.';

export const Presets: FC = () => {
	const dispatch = useTypedDispatch();

	const { getContent } = useContent();

	const { selectedProductModuleCollection, selectedPreset, options, project } =
		useTypedSelector((app) => app.creator);

	const startCollectionMutation = useStartCollectionMutation();
	const clearProjectMutation = useMutation({
		mutationFn: ProjectApi.clear,
		onSuccess: () => {
			dispatch(
				creatorActions.updateProject({ price: 0, creatorProductModules: [] })
			);
		},
	});

	const presetsQuery = useQuery({
		queryKey: [
			PresetApi.queryKey,
			{
				sortBy: {
					key: 'listOrder',
					order: 'ASC',
				},
			},
			{
				'filter.productModuleCollection.id':
					selectedProductModuleCollection?.id,
			},
		],
		queryFn: PresetApi.getAll,
		enabled: !!selectedProductModuleCollection,
	});

	const projectFromPresetMutation = useMutation({
		mutationFn: ProjectApi.createFromPreset,
		onSuccess: (data) => {
			dispatch(setLoading(false));
			dispatch(creatorActions.createFromPreset(data));
		},
	});

	const handleSelectPreset = (presetId: UUID | null) => () => {
		if (presetId) {
			dispatch(setLoading(true));
			projectFromPresetMutation.mutate({
				presetId,
				projectId: project.id,
			});
		} else {
			dispatch(
				creatorActions.updateCreator({
					selectedPreset: null,
				})
			);
			if (selectedProductModuleCollection) {
				clearProjectMutation.mutate(project.id, {
					onSuccess: () => {
						startCollectionMutation.mutate(selectedProductModuleCollection.id);
					},
				});
			}
		}
	};

	useLayoutEffect(() => {
		if (!options.lockedPresetId || !project.id) return;

		handleSelectPreset(options.lockedPresetId)();
	}, [options.lockedPresetId, project.id]);

	if (options.isPresetMode || options.isPreviewMode) return null;

	return options.lockedPresetId ? null : (
		<Accordion.Item value="presets">
			<Accordion.Control
				disabled={!selectedProductModuleCollection}
				icon={
					selectedProductModuleCollection && (
						<Badge>
							<Text size="sm">
								{selectedPreset
									? selectedPreset.name
									: getContent(`${baseSlug}ownProjectButtonText`)}
							</Text>
						</Badge>
					)
				}
			>
				{getContent(`${baseSlug}sets`)}
			</Accordion.Control>
			<Accordion.Panel>
				<SimpleGrid
					py="md"
					cols={{
						base: 2,
						sm: 3,
					}}
					spacing="xs"
				>
					<AccordionOptionCard
						imagePath={getContent(`${baseSlug}ownProjectButtonImage`) as string}
						isSelected={!selectedPreset}
						name={getContent(`${baseSlug}ownProjectButtonText`) as string}
						onSelect={handleSelectPreset(null)}
					/>
					{presetsQuery.data?.map((preset) => {
						const isSelected = selectedPreset?.id === preset.id;

						return (
							<AccordionOptionCard
								key={preset.id}
								id={preset.id}
								isSelected={isSelected}
								name={preset.name}
								imagePath={getFileSrc(preset?.image?.path)}
								onSelect={handleSelectPreset(preset.id)}
							/>
						);
					})}
				</SimpleGrid>
			</Accordion.Panel>
		</Accordion.Item>
	);
};
